import { InputAdornment, type InputProps, TextField, type TextFieldProps } from '@mui/material';
import type { ChangeEvent } from 'react';
import { InfoSolidError } from '@/components/Icons/SystemIcons';
import { formatCurrency } from '@/utils';
import CurrencyInputIcon from './CurrencyInputIcon';

interface CurrencyInputProps extends Omit<TextFieldProps, 'value' | 'onChange' | 'inputProps'> {
    value?: string | number | null;
    onChange?: (value: string | number) => void;
    label?: string;
    inputProps?: InputProps;
    tabindex?: string | number;
    allowDecimal?: boolean;
    showErrorAdornment?: boolean;
}

export default function CurrencyInput({
    tabindex,
    label,
    value,
    onChange,
    inputProps,
    allowDecimal = false,
    showErrorAdornment = false,
    ...rest
}: CurrencyInputProps) {
    const handleInputChange = (event: ChangeEvent<HTMLInputElement>): void => {
        let rawStr = event.target?.value?.toString();
        // Remove preceding zeros
        rawStr = rawStr.replace(/^0+(?=\d)/, '');
        // If allowDecimal is not set then do not allow the user to use the .
        if (!allowDecimal && rawStr.includes('.')) return;
        // Replacing all the commas so we can again format the numbers
        const resultStr = rawStr?.replace(/,/g, '');
        onChange?.(formatCurrency(resultStr) || formatCurrency(0));
    };

    return (
        <TextField
            {...rest}
            label={label}
            value={formatCurrency(value?.toString())}
            onChange={handleInputChange}
            InputProps={{
                ...inputProps,
                tabIndex: tabindex ? parseInt(`${tabindex}`, 10) : undefined,
                startAdornment: (
                    <InputAdornment position="start">{<CurrencyInputIcon />}</InputAdornment>
                ),
                endAdornment: (
                    <>
                        {inputProps?.endAdornment ||
                            (rest.error && showErrorAdornment && (
                                <InputAdornment position="end">
                                    <InfoSolidError />
                                </InputAdornment>
                            ))}
                    </>
                ),
            }}
        />
    );
}
